/**
 * CLI Auth Page — browser-side of the SDK browser login flow.
 *
 * Reached via /auth/cli?port={port}. After the user logs in,
 * this page auto-generates an API key and POSTs it back to the SDK's
 * local callback server on localhost:{port}/callback.
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authClient } from "../lib/auth";
import { createApiKey, type CreateKeyPayload, type CreatedApiKey } from "../api/apiKeys";

type Status = "waiting" | "creating_key" | "sending" | "done" | "error";
type CliKeyScope = NonNullable<CreateKeyPayload["scope"]>;

const VALID_SCOPES = new Set<CliKeyScope>(["full", "embed", "agent"]);

export function cliApiKeyPayload(searchParams: URLSearchParams, hostname: string): CreateKeyPayload {
  const requestedScope = searchParams.get("scope")?.toLowerCase();
  const scope: CliKeyScope = VALID_SCOPES.has(requestedScope as CliKeyScope)
    ? (requestedScope as CliKeyScope)
    : "full";
  const projectId = searchParams.get("project_id") ?? searchParams.get("project");
  const isProjectScoped = scope === "embed" || scope === "agent";
  if (isProjectScoped && !projectId) {
    throw new Error(`project_id is required when scope=${scope}`);
  }
  const label = scope === "agent" ? "Agent login" : scope === "embed" ? "Embed login" : "CLI login";
  const name =
    searchParams.get("name") ??
    `${label} (${hostname}${isProjectScoped && projectId ? `, ${projectId.slice(0, 8)}` : ""}, ${new Date().toISOString().slice(0, 16)})`;
  return {
    name,
    scope,
    ...(isProjectScoped && projectId ? { project_id: projectId } : {}),
  };
}

export function cliCallbackUrl(port: string, created: CreatedApiKey): string {
  const callback = new URL(`http://localhost:${port}/callback`);
  callback.searchParams.set("api_key", created.raw_key);
  callback.searchParams.set("scope", created.scope ?? "full");
  if (created.project_id) callback.searchParams.set("project_id", created.project_id);
  if (created.expires_at) callback.searchParams.set("expires_at", created.expires_at);
  return callback.toString();
}

export default function CLIAuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const port = searchParams.get("port");
  const [status, setStatus] = useState<Status>("waiting");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!port) {
      setError("Missing port parameter. Please retry from the SDK.");
      setStatus("error");
      return;
    }
    const callbackPort = port;

    async function handleAuth() {
      const { data: { session } } = await authClient.auth.getSession();
      if (!session) {
        // Save the full CLI return URL (including the `/thread` basename in
        // prod) so AuthCallback can bounce back here via
        // window.location.assign(). Storing the absolute pathname avoids
        // React Router's basename quirks on the round-trip — see
        // AuthCallback.tsx and BobbinConnect.tsx for the matching consumer.
        const fullPath = `${window.location.pathname}${window.location.search}`;
        sessionStorage.setItem("cli_auth_return", fullPath);
        navigate("/", { replace: true });
        return;
      }

      try {
        setStatus("creating_key");
        const hostname = new URL(window.location.href).hostname;
        const created = await createApiKey(cliApiKeyPayload(searchParams, hostname));

        setStatus("sending");
        // Navigate instead of fetch — browsers block HTTPS→HTTP fetch (mixed content)
        // but allow normal navigations to localhost.
        window.location.href = cliCallbackUrl(callbackPort, created);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to complete CLI login");
        setStatus("error");
      }
    }

    handleAuth();
  }, [navigate, port, searchParams]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        gap: 16,
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      {status === "waiting" && (
        <p style={{ color: "var(--gray-9, #6b7280)" }}>Checking authentication...</p>
      )}
      {status === "creating_key" && (
        <p style={{ color: "var(--gray-9, #6b7280)" }}>Creating API key...</p>
      )}
      {status === "sending" && (
        <p style={{ color: "var(--gray-9, #6b7280)" }}>Sending key to SDK...</p>
      )}
      {status === "done" && (
        <>
          <div style={{ fontSize: 48 }}>&#x2713;</div>
          <p style={{ fontSize: 18, fontWeight: 600, color: "var(--green-9, #16a34a)" }}>
            Authenticated! You can close this tab.
          </p>
          <p style={{ color: "var(--gray-9, #6b7280)", fontSize: 14 }}>
            Your API key has been saved to ~/.config/thread/credentials.toml
          </p>
        </>
      )}
      {status === "error" && (
        <>
          <p style={{ color: "var(--red-9, #dc2626)" }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid var(--gray-7, #d1d5db)",
              background: "var(--gray-3, #f3f4f6)",
              color: "var(--gray-12, #111827)",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </>
      )}
    </div>
  );
}
